import { useTranslation } from "react-i18next";

import { graphql } from "relay-runtime";
import RelayModernEnvironment from "relay-runtime/lib/store/RelayModernEnvironment";
import { fetchQuery } from "relay-hooks";
import {
  exportCardsQuery,
  exportCardsQuery$variables,
  exportCardsQuery$data
} from "./__generated__/exportCardsQuery.graphql";
import { useViewerDecks } from "queries/viewerQuery";
import { useEnvironment } from "graphql/environment";
import React from "react";
import { isApp } from "tools/device";

const cardExportQL = graphql`
  query exportCardsQuery($count: Int!, $deckId: ID!) {
    node(id: $deckId) {
      ... on Deck {
        cards(first: $count) {
          edges {
            node {
              id
              front
              back
              hint
              publishedAt
            }
          }
        }
      }
    }
  }
`;

const convertToCSV = (data: { [key: string]: any }[]): string => {
  if (!data || data.length === 0) return "";
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map((row) =>
      headers.map((fieldName) => JSON.stringify(row[fieldName], (_, value) => (value === null ? "" : value))).join(",")
    )
  ];
  return csvRows.join("\n");
};

const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const exportCards = (environment: RelayModernEnvironment, args: exportCardsQuery$variables) =>
  new Promise<exportCardsQuery$data["node"] | null>(async (done) => {
    fetchQuery<exportCardsQuery>(environment, cardExportQL, args).subscribe({
      next: (data) => done(data.node)
    });
  });

const useExportCards = () => {
  const { deck } = useViewerDecks();
  const environment = useEnvironment();
  const { t } = useTranslation();

  return React.useCallback(() => {
    if (isApp())
      return alert(t("Exporting cards is available only in the browser on <Web>").replace("<Web>", "app.duocards.com"));

    const cardsPromise = exportCards(environment, {
      count: 10_000,
      deckId: deck!.id
    });
    cardsPromise.then((data) => {
      if (data?.cards?.edges) {
        const cardDataForCSV = data.cards.edges
          .map((edge) => edge?.node)
          .map((node) => ({
            front: node?.front,
            back: node?.back,
            hint: node?.hint,
            publishedAt: node?.publishedAt
          }));
        if (cardDataForCSV.length > 0) {
          const csvString = convertToCSV(cardDataForCSV as any);
          downloadFile(csvString, `duo_cards_${deck?.front}_export.csv`, "text/csv;charset=utf-8;");
        }
      }
    });
  }, [environment, deck, t]);
};

export default useExportCards;
